import { CommentsApi } from 'api/CommentsApi';
import { IComment } from 'app/components/CommentsContainer/IComment';
import { selectPublicUrl } from 'app/slice/selectors';
import { CommentReferenceTypes } from 'enums/commentReferenceTypes';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as React from 'react';
import { WorkOrderDetailsState } from '../slice/types';
import { useAsyncGetWthEffect } from 'app/hooks/useAsyncGetOnMount';
import { CommentsContainer } from 'app/components/CommentsContainer';
import { translations } from 'locales/translations';
import { buildURL } from 'utils/url-utils';
import { Portal } from '@material-ui/core';
import { AuthenticatedUser } from 'types/AuthenticatedUser';
import { useUploadToNewComment } from 'app/components/LargeFilesUpload/Components/useUploadToNewComment';
import { useLargeFilesUploadSlice } from 'app/components/LargeFilesUpload/slice';
import { selectLargeFilesUploading } from 'app/components/LargeFilesUpload/slice/selectoes';

export interface WorkOrderCommentsProps {
  isAdmin?: boolean;
  order: WorkOrderDetailsState;
  disabled?: boolean;
  commentsFormRef: React.MutableRefObject<any>;
  isEdit?: boolean;
  user?: AuthenticatedUser;
  sectionTitle?: React.ReactNode;
  onNewCommmentAdded?: (commentId: number) => void;
  onCommmentsReady?: () => void;
}

export const WorkOrderComments = (props: WorkOrderCommentsProps) => {
  const {
    order,
    isAdmin,
    disabled,
    commentsFormRef,
    isEdit,
    sectionTitle,
    onNewCommmentAdded,
    onCommmentsReady,
  } = props;
  const publicUrl = useSelector(selectPublicUrl);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { actions: largeFileActions } = useLargeFilesUploadSlice();
  const [changed, setchanged] = React.useState(false);
  const id = isEdit ? order.Id : -1;
  const [newComments, setNewComments] = React.useState<IComment[]>([]);
  const comments = useAsyncGetWthEffect<IComment[]>(
    () => CommentsApi.GetComments(CommentReferenceTypes.Alerts, order.Id),
    [],
    [changed],
  );
  const bindSubmitComment = React.useCallback(
    submitForm => {
      commentsFormRef.current = submitForm;
    },
    [commentsFormRef],
  );
  const { onCommentAdded } = useUploadToNewComment();
  const isUploading = useSelector(selectLargeFilesUploading);
  const [formSubmitted, setFormSubitted] = React.useState(false);
  React.useEffect(() => {
    if (formSubmitted && !isUploading) {
      onCommmentsReady?.();
      setFormSubitted(false);
    }
  }, [formSubmitted, isUploading, onCommmentsReady]);
  React.useEffect(() => {
    dispatch(
      largeFileActions.setCommentProperties({
        commentType: CommentReferenceTypes.Alerts,
        referenceId: id,
      }),
    );
  }, [dispatch, largeFileActions, id]);

  const portalRef = React.useRef(null);
  return (
    <React.Fragment>
      <div ref={portalRef} style={{ width: '100%' }} />
      <Portal container={portalRef.current}>
        <CommentsContainer
          disabled={disabled}
          Comments={isEdit ? comments : newComments}
          sectionTitle={sectionTitle}
          bindSubmitComment={bindSubmitComment}
          onSubmit={(c, isInner) => {
            if (!disabled) {
              if (isEdit) {
                CommentsApi.SendComment(
                  CommentReferenceTypes.Alerts,
                  order.Id,
                  c,
                )
                  .then(res => onCommentAdded?.(res))
                  .finally(() => setchanged(prev => !prev));
              } else {
                CommentsApi.SendComment(CommentReferenceTypes.Alerts, -1, c)
                  .then(res => {
                    onNewCommmentAdded?.(res.Id);
                    onCommentAdded?.(res);
                    if (!isInner) {
                      setTimeout(() => {
                        setFormSubitted(true);
                      }, 5);
                    }
                    return CommentsApi.GetComments(
                      CommentReferenceTypes.Alerts,
                      -1,
                      res.Id,
                    );
                  })
                  .then(c => {
                    setNewComments(prev => [...prev, ...c]);
                  });
              }
            }
          }}
          isAdmin={isAdmin}
          withMentions
          pageName={t(translations.menu_EditWorkOrder)}
          referenceName={order.Id.toString()}
          linkUrl={buildURL(`${publicUrl}workorders/details/${order.Id}`, {})}
          emailType={'Alert'}
        />
      </Portal>
    </React.Fragment>
  );
};
